import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bike, ShoppingBag, Pizza, Coffee, MapPin, ChevronRight } from 'lucide-react';

/**
 * Entry experience for the dedicated ordering page: a 3s animated food-delivery
 * splash, then the customer chooses Asporto (pickup) or Domicilio (delivery)
 * before the menu is revealed. If only one mode is enabled it is auto-selected.
 */
export const OrderingIntro = ({ config, restaurantName, onSelect }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('splash'); // splash | choose
  const takeaway = !!config?.takeaway_enabled;
  const delivery = !!config?.delivery_enabled;

  useEffect(() => {
    const id = setTimeout(() => {
      // Auto-pick when a single channel is active.
      if (takeaway && !delivery) return onSelect('asporto');
      if (delivery && !takeaway) return onSelect('domicilio');
      setPhase('choose');
    }, 3000);
    return () => clearTimeout(id);
  }, [takeaway, delivery, onSelect]);

  if (phase === 'splash') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden"
        style={{ background: 'radial-gradient(120% 120% at 50% 10%, hsl(24 95% 56%) 0%, hsl(20 90% 44%) 45%, hsl(20 60% 22%) 100%)' }}
        data-testid="ordering-intro-splash">
        {/* floating food icons */}
        <div className="absolute inset-0 pointer-events-none">
          {[{ I: Pizza, l: '24%', d: '.1s' }, { I: ShoppingBag, l: '50%', d: '.5s' }, { I: Coffee, l: '72%', d: '.9s' }].map(({ I, l, d }, i) => (
            <span key={i} className="absolute bottom-[42%] text-white/90" style={{ left: l, animation: `introFloatUp 2.4s ease-in ${d} infinite` }}>
              <I className="h-7 w-7" />
            </span>
          ))}
        </div>

        {/* scooter scene */}
        <div className="relative">
          <div className="absolute -inset-10 rounded-full bg-white/15 blur-2xl" style={{ animation: 'introGlow 2s ease-in-out infinite' }} />
          <div className="relative" style={{ animation: 'introRide 1.3s cubic-bezier(.2,.7,.2,1) both' }}>
            <div style={{ animation: 'introBob 0.7s ease-in-out 1.3s infinite' }}>
              <div className="flex h-28 w-28 items-center justify-center rounded-[28px] bg-white shadow-2xl">
                <Bike className="h-16 w-16" style={{ color: 'hsl(20 90% 48%)' }} />
              </div>
            </div>
            {/* speed trails */}
            <span className="absolute right-full top-8 mr-2 h-1 w-10 rounded-full bg-white/70" style={{ animation: 'introTrail .6s ease-out 1.4s infinite' }} />
            <span className="absolute right-full top-14 mr-2 h-1 w-7 rounded-full bg-white/50" style={{ animation: 'introTrail .6s ease-out 1.55s infinite' }} />
          </div>
        </div>

        {/* moving road */}
        <div className="mt-6 h-1.5 w-56 rounded-full" style={{ background: 'repeating-linear-gradient(90deg, rgba(255,255,255,.85) 0 20px, transparent 20px 40px)', animation: 'introRoad .5s linear infinite' }} />

        <div className="mt-8 text-center px-6" style={{ animation: 'introFadeUp .6s ease-out .6s both' }}>
          <div className="font-display text-2xl font-black text-white tracking-tight">{restaurantName}</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-white/90 text-sm font-medium">
            <ShoppingBag className="h-4 w-4" /> {t('offsite.type.asporto')} · <Bike className="h-4 w-4" /> {t('offsite.type.domicilio')}
          </div>
        </div>
      </div>
    );
  }

  const Card = ({ type, Icon }) => (
    <button
      onClick={() => onSelect(type)}
      data-testid={`intro-choice-${type}`}
      className="group relative flex w-full items-center gap-4 overflow-hidden rounded-3xl border border-border bg-card p-5 text-left shadow-sm-token transition-all hover:-translate-y-0.5 hover:shadow-lg-token hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]"
    >
      <span className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/10 transition-transform group-hover:scale-150" />
      <span className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
        <Icon className="h-8 w-8" />
      </span>
      <span className="relative min-w-0 flex-1">
        <span className="block font-display text-xl font-black leading-tight">{t(`offsite.type.${type}`)}</span>
        <span className="block text-sm text-muted-foreground">{t(`offsite.typeHint.${type}`)}</span>
      </span>
      <ChevronRight className="relative h-6 w-6 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background px-6" data-testid="ordering-intro-choose">
      <div className="w-full max-w-md" style={{ animation: 'introFadeUp .4s ease-out both' }}>
        <div className="mb-7 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-lg">
            <MapPin className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight">{t('offsite.chooseType')}</h1>
          <p className="mt-1 text-muted-foreground">{restaurantName}</p>
        </div>
        <div className="space-y-3">
          {takeaway && <Card type="asporto" Icon={ShoppingBag} />}
          {delivery && <Card type="domicilio" Icon={Bike} />}
        </div>
      </div>
    </div>
  );
};
